import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import api from '../utils/api';
import { toast } from 'sonner';
import { ArrowLeft, CreditCard, Check, Zap } from 'lucide-react';

const PurchaseCredits = ({ setAuth }) => {
  const navigate = useNavigate();
  const [packages, setPackages] = useState({});
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    fetchPackages();
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await api.get('/credit-packages');
      setPackages(response.data.packages);
    } catch (error) {
      toast.error('Failed to load packages');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = (packageId) => {
    toast.info('Stripe integration requires API keys. Please configure Stripe in Settings.');
    // In production, this would initiate Stripe payment flow
    // const response = await api.post('/create-payment-intent', { package_id: packageId });
    // Then redirect to Stripe checkout
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center space-x-4">
            <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')} className="border-slate-300">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-slate-800">Purchase Credits</h1>
              <p className="text-sm text-slate-500">Your current balance: {user?.credits || 0} credits</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-slate-800 mb-3">Choose Your Credit Package</h2>
          <p className="text-slate-600">Credits are used for domain and email verification checks</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {Object.entries(packages).map(([id, pkg]) => {
            const popular = id === 'professional';
            return (
              <Card key={id} className={`relative ${popular ? 'border-2 border-blue-500 shadow-xl' : 'border-slate-200'}`}>
                {popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-blue-600 text-white px-4 py-1">
                      <Zap className="w-3 h-3 mr-1" />
                      Most Popular
                    </Badge>
                  </div>
                )}
                <CardHeader className="text-center pt-8">
                  <CardTitle className="text-2xl font-bold text-slate-800">{pkg.credits} Credits</CardTitle>
                  <CardDescription className="text-lg font-semibold text-slate-700 mt-2">
                    £{(pkg.price / 100).toFixed(2)}
                  </CardDescription>
                  <p className="text-sm text-slate-500 mt-1">
                    £{((pkg.price / 100) / pkg.credits).toFixed(2)} per credit
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2 text-sm text-slate-600">
                      <Check className="w-4 h-4 text-green-600" />
                      <span>{pkg.credits} domain checks</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-slate-600">
                      <Check className="w-4 h-4 text-green-600" />
                      <span>{pkg.credits} email verifications</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-slate-600">
                      <Check className="w-4 h-4 text-green-600" />
                      <span>Unlimited Companies House lookups</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-slate-600">
                      <Check className="w-4 h-4 text-green-600" />
                      <span>Credits never expire</span>
                    </div>
                  </div>
                  <Button
                    className={`w-full ${
                      popular
                        ? 'bg-blue-600 hover:bg-blue-700'
                        : 'bg-slate-700 hover:bg-slate-800'
                    }`}
                    onClick={() => handlePurchase(id)}
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    Purchase Now
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="mt-12 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="font-semibold text-blue-900 mb-2">What are credits used for?</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• <strong>Domain Checker:</strong> 1 credit per check - verifies DNS, MX records, and website availability</li>
            <li>• <strong>Email Checker:</strong> 1 credit per check - validates email addresses via SMTP</li>
            <li>• <strong>Free Features:</strong> Companies House lookups, manual client entry, and email templates are always free</li>
          </ul>
        </div>
      </main>
    </div>
  );
};

export default PurchaseCredits;
